import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  DEFAULT_PAGE_SIZE,
  Avatar,
  Banner,
  Breadcrumb,
  Button,
  Card,
  Chip,
  DataTable,
  DefinitionList,
  Donut,
  Drawer,
  Field,
  Heatmap,
  Icon,
  Input,
  Kpi,
  Legend,
  Menu,
  Meter,
  MoneyInput,
  Pager,
  PresetAmounts,
  RankedBars,
  RoleChip,
  Skeleton,
  StateBlock,
  Tabs,
  TrendChart,
  seriesColor,
  useToast,
  type Column,
  type Crumb,
  type TabItem,
  type Tone,
} from '@w7/ui'
import {
  useAccount,
  useActivity,
  useBets,
  useMemberDetail,
  useSession,
  useStatement,
  useTransferCredit,
  useTransfers,
} from '@/app/queries'
import { useDateRange } from '@/app/range'
import { useMemberAccountPoll } from '@/features/member/useMemberAccountPoll'
import { Amount, AsOf, FlagChips, MiniSpark, Money, exportCsv } from '@/app/bits'
import { ExportCsvButton } from '@/app/ExportCsvButton'
import { availableToGive, limitUsage, round2 } from '@/lib/book'
import { convertDisplayAmount, parseCurrencyType } from '@/lib/currency'
import {
  EMPTY,
  count,
  dayLabel,
  displayMoneyCompact,
  lll,
  money,
  odds,
  pct,
  percent,
  sharePoints,
} from '@/lib/format'
import { GAME_LABEL, ROLE_LABEL, isAgent } from '@/data/types'
import type { Account, AccountStatus, ActivityKind, Bet, DateRange, GameSwitches } from '@/data/types'
import { usePlayerMfaUser } from '@/features/player-2fa/hooks'
import { useAgentMfaStatus } from '@/features/player-2fa/agentHooks'
import { ConsoleMfaChip, PlayerPortalMfaChip } from '@/features/player-2fa/MfaStatusChip'
import { EditAccountTab } from '@/features/member/EditAccountTab'

/**
 * Member 360 — one page for one account in the downline.
 *
 * The product being replaced answered "who is this and what have they done?"
 * with seven unlabelled icon columns on a list row (Info, Detail, Action, Lock,
 * Events, Insights, Commission), each opening a different modal, none of them
 * hoverable and none of them explaining what they meant. Everything those icons
 * hid lives here under a heading, in one place, with the upline chain visible so
 * an operator can see whose member this is.
 *
 * The route is `/member/:id` for every rung of the ladder, not just the leaf —
 * an agent's page shows the same six figures aggregated over the members
 * beneath them.
 */

/* The screen never imports the data client. Taking the shape from the hook that
   returns it keeps that boundary honest and still gives the sub-components a
   real type. */
type MemberDetail = NonNullable<ReturnType<typeof useMemberDetail>['data']>
type Range = DateRange & { label: string; days: number }

type TabId = 'overview' | 'edit' | 'bets' | 'statement' | 'activity'

const TABS: TabItem<TabId>[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'edit', label: 'Edit' },
  { id: 'bets', label: 'Bets' },
  { id: 'statement', label: 'Statement' },
  { id: 'activity', label: 'Activity' },
]

const isTabId = (value: string | null): value is TabId =>
  TABS.some((tab) => tab.id === value)

/** Status is not money, so it never borrows the profit/loss colours. */
const STATUS: Record<AccountStatus, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'info' },
  inactive: { label: 'Inactive', tone: 'muted' },
  suspended: { label: 'Suspended', tone: 'warn' },
  closed: { label: 'Closed', tone: 'muted' },
}

const BET_STATUS: Record<Bet['status'], { label: string; tone: Tone }> = {
  unmatched: { label: 'Unmatched', tone: 'warn' },
  matched: { label: 'Matched', tone: 'info' },
  settled: { label: 'Settled', tone: 'muted' },
  void: { label: 'Void', tone: 'muted' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
}

const ACTIVITY_LABEL: Record<ActivityKind, string> = {
  transfer: 'Credit',
  'account-created': 'Created',
  'limit-changed': 'Limits',
  'status-changed': 'Status',
  login: 'Sign-in',
  'password-reset': 'Password',
  settlement: 'Settlement',
}

const GAME_KEYS = Object.keys(GAME_LABEL) as (keyof GameSwitches)[]

/**
 * A zero bet limit means "not set at this rung", not "cannot stake a rupee" —
 * agent tiers carry no per-bet cap of their own. Rendering it as ₹0 was how the
 * old settings modal made every Master look like they were blocked from betting.
 */
function betLimitLabel(account: Account): string {
  const { min, max } = account.betLimits
  const ccy = parseCurrencyType(account.currencyType)
  const fmt = (n: number) => money(n, { round: true, currency: ccy, alreadyDisplay: true })
  if (min <= 0 && max <= 0) return 'Not set at this level'
  if (max <= 0) return `${fmt(min)} minimum, no maximum set`
  return `${fmt(min)} – ${fmt(max)}`
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'))

export function MemberPage() {
  const { id } = useParams<{ id: string }>()
  const { range } = useDateRange()
  const navigate = useNavigate()

  /**
   * The tab lives in the URL, not in local state.
   *
   * Three callers already build `/member/:id?tab=…` — the Downline row menu, the
   * palette's "Open X's statement" / "Open X's bets" rows, and every transfer
   * hit, which also carries `?ref=` so it can land on the movement it matched.
   * All of them silently landed on Overview while this was `useState`, which
   * made the row menu's "View statement" indistinguishable from "View member".
   * An unknown `?tab=` falls back rather than throwing: a stale link is a normal
   * thing to receive.
   */
  const [params, setParams] = useSearchParams()
  const tabParam = params.get('tab')
  const tab: TabId = isTabId(tabParam) ? tabParam : 'overview'
  const highlightRef = params.get('ref')

  const setTab = useCallback(
    (next: TabId) => {
      const nextParams = new URLSearchParams(params)
      if (next === 'overview') nextParams.delete('tab')
      else nextParams.set('tab', next)
      // A ref only means something on the statement it was found in. Carrying it
      // onto the Bets tab would leave a callout pointing at a row that is not
      // in the table underneath it.
      if (next !== 'statement') nextParams.delete('ref')
      // replace: four tabs must not cost four presses of the back button.
      setParams(nextParams, { replace: true })
    },
    [params, setParams],
  )

  /* One idempotency key per opened drawer: a double-tapped Confirm, or a retry
     after a timeout, must move the money exactly once. */
  const [pending, setPending] = useState<{ direction: 'give' | 'take'; key: string } | null>(null)

  const detailQuery = useMemberDetail(id, range)
  const accountQuery = useAccount(id)
  const sessionQuery = useSession()

  /* Exposure / balances move while this page is open — always poll (no credit SSE). */
  useMemberAccountPoll(accountQuery.refetch)

  const detail = detailQuery.data
  const account = accountQuery.data
  const self = sessionQuery.data?.account

  const playerMfaQuery = usePlayerMfaUser(
    account?.username,
    Boolean(account && account.role === 'player'),
  )
  const agentMfaQuery = useAgentMfaStatus(
    account?.username,
    Boolean(account && isAgent(account.role)),
    account?.id,
  )

  const crumbs = useMemo<Crumb[]>(() => {
    if (!detail || !account) return []
    /* `upline` is the full ancestor chain from the world root. Anything above
       the signed-in account is out of subtree and must not appear — naming it
       would confirm it exists, which is exactly what scoping prevents. */
    const start = self ? detail.upline.findIndex((a) => a.id === self.id) : -1
    const visible = start >= 0 ? detail.upline.slice(start) : []
    return [
      ...visible.map((a) => ({ label: a.username, href: a.id === self?.id ? '/' : `/member/${a.id}` })),
      { label: account.username },
    ]
  }, [detail, account, self])

  const openEdit = () => setTab('edit')

  if (detailQuery.isLoading || accountQuery.isLoading) return <MemberSkeleton />

  if (!detail || !account) {
    if (detailQuery.isError || accountQuery.isError) {
      const failure = (detailQuery.error ?? accountQuery.error) as Error | null
      return (
        <StateBlock
          icon="warning"
          title="No data found"
          actions={
            <Button
              variant="secondary"
              icon="refresh"
              onClick={() => {
                void detailQuery.refetch()
                void accountQuery.refetch()
              }}
            >
              Try again
            </Button>
          }
        >
          {failure?.message ||
            'This account could not be reached. The layout stays ready — nothing is invented while hierarchy is down.'}
        </StateBlock>
      )
    }
    /* ABSENT, not forbidden. A "you do not have access" message would confirm
       that something exists at this id, which is the one thing subtree scoping
       is there to prevent. */
    return (
      <StateBlock
        icon="search"
        title="Nothing here"
        actions={
          <Button variant="secondary" icon="users" onClick={() => navigate('/aggregate/accounts')}>
            Browse Aggregate Accounts
          </Button>
        }
      >
        This link does not point at anyone in your downline. It may be stale, or it may have been
        copied from another agent&rsquo;s console.
      </StateBlock>
    )
  }

  const status = STATUS[account.status]
  const ccy = parseCurrencyType(account.currencyType)
  const dm = (n: number | null | undefined, extra: Parameters<typeof money>[1] = {}) =>
    money(n, { ...extra, currency: ccy, alreadyDisplay: true })
  const dmc = (n: number | null | undefined) => displayMoneyCompact(n, ccy)
  const usage = limitUsage(account.exposure, account.creditLimit)
  const lifetime = detail.lifetime
  const rangeTurnover = detail.daily.reduce((sum, d) => sum + d.turnover, 0)
  const rangeBets = detail.daily.reduce((sum, d) => sum + d.bets, 0)
  const winRate = lifetime.settledBets > 0 ? lifetime.wins / lifetime.settledBets : null

  /* Member P&L is the member's own result, so its sign is the mirror of the
     book's: a member losing is the book winning. Colour follows the book. */
  const memberPnlTone: Tone =
    lifetime.memberPnl > 0 ? 'loss' : lifetime.memberPnl < 0 ? 'profit' : 'neutral'

  /* Credit only moves one rung at a time — the ledger rejects anything else, so
     the buttons say why rather than failing at Confirm. */
  const canMoveCredit = Boolean(self) && account.parentId === self?.id
  const cannotMoveReason = `Credit moves one level at a time. Only ${account.username}'s direct upline can give or take.`

  return (
    <>
      <div className="w7-page-head">
        {/* Top-aligned: the identity block wraps onto three or four lines on a
            phone, and a vertically-centred avatar drifts away from the name. */}
        <div className="w7-row" style={{ gap: 'var(--w7-s4)', minWidth: 0, alignItems: 'flex-start' }}>
          <Avatar name={account.displayName || account.username} size="lg" />
          <div style={{ minWidth: 0 }}>
            <div className="w7-row w7-row-wrap" style={{ gap: 8 }}>
              <h1 className="w7-page-title">{account.username}</h1>
              <RoleChip role={account.role} label={ROLE_LABEL[account.role]} />
              <Chip tone={status.tone} size="sm">
                {status.label}
              </Chip>
              {account.role === 'player' ? (
                <PlayerPortalMfaChip
                  status={playerMfaQuery.data}
                  loading={playerMfaQuery.isLoading}
                />
              ) : isAgent(account.role) ? (
                <ConsoleMfaChip status={agentMfaQuery.data} loading={agentMfaQuery.isLoading} />
              ) : null}
              <FlagChips flags={account.flags} />
            </div>
            <p className="w7-card-sub">
              {account.displayName}
              {isAgent(account.role) ? ` · ${count(account.memberCount)} members beneath` : ''} · joined{' '}
              {lll(account.createdAt)}
            </p>
            {crumbs.length > 1 ? <Breadcrumb items={crumbs} onNavigate={(href) => navigate(href)} /> : null}
          </div>
        </div>

        <div className="w7-row w7-row-wrap w7-page-head-trail" style={{ marginLeft: 'auto', gap: 'var(--w7-s2)' }}>
          <AsOf at={accountQuery.dataUpdatedAt || undefined} polls />
          <Button
            icon="give"
            variant="secondary"
            disabled={!canMoveCredit}
            title={canMoveCredit ? undefined : cannotMoveReason}
            onClick={() => setPending({ direction: 'give', key: `give-${account.id}-${Date.now()}` })}
          >
            Give
          </Button>
          <Button
            icon="take"
            variant="secondary"
            disabled={!canMoveCredit}
            title={canMoveCredit ? undefined : cannotMoveReason}
            onClick={() => setPending({ direction: 'take', key: `take-${account.id}-${Date.now()}` })}
          >
            Take
          </Button>
          <Menu
            label="More actions"
            align="end"
            items={[
              { id: 'edit', label: 'Edit account', icon: 'edit', onSelect: openEdit },
              { id: 'limits', label: 'Change limits', icon: 'sliders', onSelect: openEdit },
              { id: 'password', label: 'Reset password', icon: 'lock', onSelect: openEdit },
              'separator',
              {
                id: 'suspend',
                label: 'Suspend',
                icon: 'lock',
                tone: 'danger',
                onSelect: openEdit,
              },
            ]}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------- KPIs -- */}
      <div
        className="w7-grid w7-grid-6 w7-member-kpis"
        data-tab={tab}
        style={{ marginBottom: 'var(--w7-s5)' }}
      >
        <Kpi
          label="Live balance"
          value={dmc(account.balance)}
          exact={dm(account.balance)}
          accent
          meta={`${dm(availableToGive(account.balance, account.exposure), { round: true })} of it uncommitted`}
        />
        <Kpi
          label="Total balance"
          value={dmc(account.creditLimit)}
          exact={dm(account.creditLimit)}
          meta={
            account.role === 'player'
              ? crumbs.length > 1
                ? `Set by ${crumbs[crumbs.length - 2]?.label ?? EMPTY}`
                : 'Ceiling this account may hold'
              : 'Credit line assigned to this agent'
          }
        />
        {account.role !== 'player' ? (
          <Kpi
            label="Distributed balance"
            value={dmc(account.distributedBalance)}
            exact={dm(account.distributedBalance)}
            meta={`${dm(Math.max(0, account.creditLimit - account.distributedBalance), { round: true })} remaining to assign`}
          />
        ) : null}
        <Kpi
          label="Open exposure"
          value={dmc(account.exposure)}
          exact={dm(account.exposure)}
          tone={usage > 1 ? 'loss' : usage > 0.8 ? 'warn' : 'neutral'}
          meta={
            <>
              <Meter value={usage} label={`${percent(usage, 0)} of credit used`} />
              {account.role === 'player'
                ? `${percent(usage, 0)} of the limit`
                : `${percent(usage, 0)} of limit · your share of player bets`}
            </>
          }
        />
        {account.role === 'player' ? (
          <>
            <Kpi
              label="Member P&L"
              value={money(lifetime.memberPnl, { signed: true, round: true, currency: ccy })}
              exact={money(lifetime.memberPnl, { signed: true, currency: ccy })}
              tone={memberPnlTone}
              size="sm"
              meta="Lifetime, the member's own result. Negative means they lost — money the book won."
            />
            <Kpi
              label="Turnover"
              value={money(rangeTurnover, { currency: ccy, round: true })}
              exact={money(rangeTurnover, { currency: ccy })}
              spark={detail.daily.length > 1 ? <MiniSpark values={detail.daily.map((d) => d.turnover)} /> : undefined}
              meta={`${count(rangeBets)} bets · ${range.label}`}
            />
            <Kpi
              label="Win rate"
              value={winRate === null ? EMPTY : percent(winRate, 1)}
              meta={
                winRate === null
                  ? 'No bets have settled yet'
                  : `${count(lifetime.wins)} won of ${count(lifetime.settledBets)} settled bets, lifetime`
              }
            />
          </>
        ) : (
          <>
            <Kpi
              label="Free to give"
              value={dmc(availableToGive(account.balance, account.exposure))}
              exact={dm(availableToGive(account.balance, account.exposure))}
              meta="Live balance less open exposure"
            />
            <Kpi
              label="Members"
              value={count(account.memberCount)}
              meta={`${count(account.childCount)} direct children`}
            />
          </>
        )}
      </div>

      <Tabs items={TABS} value={tab} onChange={setTab} />

      <div style={{ marginTop: 'var(--w7-s5)' }}>
        {tab === 'overview' ? <OverviewTab detail={detail} account={account} range={range} /> : null}
        {tab === 'edit' ? <EditAccountTab account={account} self={self} /> : null}
        {tab === 'bets' ? <BetsTab account={account} range={range} /> : null}
        {tab === 'statement' ? (
          <StatementTab account={account} range={range} highlightRef={highlightRef} />
        ) : null}
        {tab === 'activity' ? <ActivityTab account={account} /> : null}
      </div>

      {pending && self ? (
        <CreditDrawer
          direction={pending.direction}
          idempotencyKey={pending.key}
          self={self}
          target={account}
          onClose={() => setPending(null)}
        />
      ) : null}
    </>
  )
}

/* ============================================================== loading == */

/** Skeletons at the real heights of what is coming, so nothing jumps on arrival. */
function MemberSkeleton() {
  return (
    <>
      <div className="w7-page-head">
        <div className="w7-row" style={{ gap: 'var(--w7-s4)' }}>
          <Skeleton width={44} height={44} radius={14} />
          <div className="w7-col" style={{ gap: 8 }}>
            <Skeleton width={190} height={24} />
            <Skeleton width={280} height={13} />
          </div>
        </div>
      </div>
      <div className="w7-grid w7-grid-6" style={{ marginBottom: 'var(--w7-s5)' }}>
        {Array.from({ length: 6 }, (_, i) => (
          <Kpi
            key={i}
            label={<Skeleton width={78} height={11} />}
            value={<Skeleton width={104} height={26} />}
            meta={<Skeleton width={132} height={11} />}
          />
        ))}
      </div>
      <div className="w7-grid w7-grid-3">
        <Card className="w7-span-2">
          <Skeleton height={250} radius={10} />
        </Card>
        <Card>
          <Skeleton height={190} radius={10} />
        </Card>
      </div>
    </>
  )
}

/* ============================================================= overview == */

function OverviewTab({
  detail,
  account,
  range,
}: {
  detail: MemberDetail
  account: Account
  range: Range
}) {
  const transfersQuery = useTransfers({
    accountId: account.id,
    range,
    pageSize: 6,
    currencyType: account.currencyType,
  })
  const categories = detail.daily.map((d) => dayLabel(d.date))
  const totalBets = detail.stakeBuckets.reduce((sum, b) => sum + b.count, 0)
  const rangeTurnover = detail.daily.reduce((sum, d) => sum + d.turnover, 0)
  const transfers = transfersQuery.data
  const ccy = parseCurrencyType(account.currencyType)
  const book = (n: number | null | undefined, extra: Parameters<typeof money>[1] = {}) =>
    money(n, { ...extra, currency: ccy })
  const credit = (n: number | null | undefined, extra: Parameters<typeof money>[1] = {}) =>
    money(n, { ...extra, currency: ccy, alreadyDisplay: true })

  return (
    <>
      <div className="w7-grid w7-grid-3" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card
          className="w7-span-2"
          title="Turnover and member P&L"
          subtitle={`Bars are daily matched volume; the line is the member's own settled result · ${range.label}`}
        >
          <TrendChart
            label="Daily turnover with daily member profit and loss"
            categories={categories}
            height={250}
            maxLabels={8}
            format={(v) => book(v, { round: true, symbol: false })}
            formatRight={(v) => book(v, { round: true, symbol: false })}
            series={[
              {
                id: 'turnover',
                label: 'Turnover',
                values: detail.daily.map((d) => d.turnover),
                kind: 'bar',
                color: 'var(--w7-c8)',
                axis: 'right',
              },
              {
                id: 'member-pnl',
                label: 'Member P&L',
                values: detail.daily.map((d) => d.memberPnl),
                kind: 'line',
                color: 'var(--w7-c1)',
              },
            ]}
            emptyMessage="Nothing was placed or settled in this period"
          />
        </Card>

        <Card title="Where they bet" subtitle={`Matched turnover by sport · ${range.label}`}>
          <Donut
            label="Turnover by sport"
            height={190}
            slices={detail.bySport.slice(0, 6).map((s) => ({ label: s.sport, value: s.turnover }))}
            format={(v) => book(v, { round: true })}
            centerValue={book(rangeTurnover, { round: true })}
            centerLabel="turnover"
            emptyMessage="No turnover in this period"
          />
          <Legend
            items={detail.bySport.slice(0, 6).map((s, i) => ({
              label: s.sport,
              color: seriesColor(i),
              value: book(s.turnover, { round: true }),
            }))}
          />
        </Card>
      </div>

      <div className="w7-grid w7-grid-2" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card title="When they bet" subtitle="Every bet ever placed, by weekday and hour of the day">
          <Heatmap
            label="Bets placed by weekday and hour"
            matrix={detail.activity}
            rows={WEEKDAYS}
            columns={HOURS}
            columnStride={3}
            height={200}
            format={(v) => `${count(v)} bets`}
            emptyMessage="No bets have been placed yet"
          />
        </Card>

        <Card
          title="Stake sizes"
          subtitle="Every bet ever placed, by stake band — a member who only ever bets one size shows as a single bar"
        >
          <RankedBars
            label="Bets by stake band"
            rows={detail.stakeBuckets.map((bucket) => ({
              label: bucket.label,
              value: bucket.count,
              color: 'var(--w7-c1)',
              note: totalBets > 0 ? percent(bucket.count / totalBets, 0) : undefined,
            }))}
            format={(v) => count(v)}
            emptyMessage="No bets have been placed yet"
          />
        </Card>
      </div>

      <div className="w7-grid w7-grid-2">
        <Card title="Settings" subtitle="What this account is allowed to do, and on what terms">
          <DefinitionList
            columns={1}
            items={[
              {
                label:
                  parseCurrencyType(account.currencyType) === 'INR' ? 'Member Rate' : 'My share',
                value: `${sharePoints(account.share.sportsbook)} / ${sharePoints(account.share.casino)}`,
              },
              { label: 'Fancy commission', value: pct(account.commission.fancy) },
              { label: 'Bookmaker commission', value: pct(account.commission.bookmaker) },
              { label: 'Match odds commission', value: pct(account.commission.matchOdds) },
              { label: 'Bet limits', value: betLimitLabel(account) },
              {
                label: 'Games enabled',
                value: (
                  <span className="w7-row w7-row-wrap" style={{ gap: 4, justifyContent: 'flex-end' }}>
                    {GAME_KEYS.filter((key) => account.games[key]).map((key) => (
                      <Chip key={key} size="sm" tone="info">
                        {GAME_LABEL[key]}
                      </Chip>
                    ))}
                    {GAME_KEYS.every((key) => !account.games[key]) ? EMPTY : null}
                  </span>
                ),
              },
              { label: 'Created', value: lll(account.createdAt) },
              { label: 'Last active', value: lll(account.lastActiveAt) },
            ]}
          />
        </Card>

        <Card
          title="Credit movements"
          subtitle={
            transfers
              ? `${credit(transfers.totals.taken, { round: true })} in, ${credit(transfers.totals.given, { round: true })} out · ${range.label}`
              : range.label
          }
          flush
        >
          {transfersQuery.isLoading ? (
            <div className="w7-col" style={{ gap: 12, padding: 'var(--w7-s4)' }}>
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} height={30} />
              ))}
            </div>
          ) : (transfers?.rows.length ?? 0) === 0 ? (
            <StateBlock icon="transfers" title="No credit has moved in this period">
              Gives and takes between {account.username} and their upline appear here. Use Give or
              Take above to move credit, or widen the date range in the top bar.
            </StateBlock>
          ) : (
            <ul className="w7-list">
              {transfers!.rows.map((t) => {
                const incoming = t.toId === account.id
                return (
                  <li key={t.id}>
                    <span className="w7-list-row">
                      <Chip tone={incoming ? 'info' : 'muted'} size="sm" icon={incoming ? 'take' : 'give'}>
                        {incoming ? 'In' : 'Out'}
                      </Chip>
                      <span style={{ minWidth: 0 }}>
                        <b>{t.remark || (incoming ? 'Credit received' : 'Credit returned')}</b>
                        <span className="w7-dim" style={{ display: 'block', fontSize: 12 }}>
                          {lll(t.at)} · ref {t.ref}
                        </span>
                      </span>
                      <Amount value={t.amount} currency={account.currencyType} alreadyDisplay />
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>
      </div>
    </>
  )
}

/* ================================================================= bets == */

function BetsTab({ account, range }: { account: Account; range: Range }) {
  const [page, setPage] = useState(1)
  const pageSize = DEFAULT_PAGE_SIZE
  const { data, isLoading } = useBets({
    accountId: account.id,
    range,
    page,
    pageSize,
  })

  const rows = data?.rows ?? []
  const pageStake = rows.reduce((sum, b) => sum + b.stake, 0)
  const pageMatched = rows.reduce((sum, b) => sum + b.matched, 0)
  const pagePnl = rows.reduce((sum, b) => sum + (b.pnl ?? 0), 0)

  const columns: Column<Bet>[] = [
    {
      id: 'placed',
      header: 'Placed',
      width: 165,
      cell: (b) => lll(b.placedAt),
      sortValue: (b) => b.placedAt,
    },
    {
      id: 'event',
      header: 'Event',
      cell: (b) => (
        <span style={{ minWidth: 0, display: 'block' }}>
          <span className="w7-truncate" style={{ display: 'block' }}>
            {b.eventName}
          </span>
          <span className="w7-dim" style={{ display: 'block', fontSize: 11.5 }}>
            {b.sport}
          </span>
        </span>
      ),
      sortValue: (b) => b.eventName,
    },
    {
      id: 'market',
      header: 'Market',
      hideBelow: 1280,
      cell: (b) => b.marketName,
      sortValue: (b) => b.marketName,
    },
    {
      id: 'selection',
      header: 'Selection',
      hideBelow: 900,
      cell: (b) => b.selection,
      sortValue: (b) => b.selection,
    },
    {
      id: 'side',
      header: 'Side',
      width: 70,
      hideBelow: 720,
      cell: (b) => (
        <Chip tone={b.side === 'back' ? 'back' : 'lay'} size="sm">
          {b.side === 'back' ? 'Back' : 'Lay'}
        </Chip>
      ),
      sortValue: (b) => b.side,
    },
    {
      id: 'odds',
      header: 'Odds',
      numeric: true,
      width: 74,
      hideBelow: 900,
      help: 'Matched odds where the bet has been matched, otherwise the odds it was placed at.',
      cell: (b) => odds(b.oddsMatched ?? b.oddsPlaced),
      sortValue: (b) => b.oddsMatched ?? b.oddsPlaced,
    },
    {
      id: 'stake',
      header: 'Stake',
      numeric: true,
      cell: (b) => (
        <Amount value={b.stake} currency={account.currencyType} alreadyDisplay={false} />
      ),
      sortValue: (b) => b.stake,
      total: (
        <Amount value={pageStake} currency={account.currencyType} alreadyDisplay={false} />
      ),
    },
    {
      id: 'matched',
      header: 'Matched',
      numeric: true,
      hideBelow: 1080,
      cell: (b) => (
        <Amount value={b.matched} currency={account.currencyType} alreadyDisplay={false} />
      ),
      sortValue: (b) => b.matched,
      total: (
        <Amount value={pageMatched} currency={account.currencyType} alreadyDisplay={false} />
      ),
    },
    {
      /* Below 720 the table keeps only when / what / how much / outcome. Six
         columns squeezed onto a phone is how the old grid ended up scrolling
         sideways at every width. */
      id: 'status',
      header: 'Status',
      width: 108,
      hideBelow: 720,
      cell: (b) => {
        const status = BET_STATUS[b.status]
        return (
          <Chip tone={status.tone} size="sm">
            {status.label}
          </Chip>
        )
      },
      sortValue: (b) => b.status,
    },
    {
      id: 'pnl',
      header: 'Member P&L',
      numeric: true,
      help: "The member's own result. Green is the book winning, red is the book paying out.",
      // Signed from the member's side, coloured from the book's — a member's
      // loss is the book's win, so the tone inverts.
      cell: (b) => (
        <Money value={b.pnl} signed invert currency={account.currencyType} />
      ),
      sortValue: (b) => b.pnl ?? null,
      total: <Money value={pagePnl} signed invert currency={account.currencyType} />,
    },
  ]


  return (
    <Card
      title="Bets"
      subtitle={`Every bet placed in ${range.label}, newest first`}
      flush
      actions={
        <ExportCsvButton
          disabled={rows.length === 0}
          count={rows.length}
          onClick={() =>
            exportCsv(
              `${account.username}-bets-${range.from}-to-${range.to}.csv`,
              rows.map((b) => ({
                Placed: lll(b.placedAt),
                Event: b.eventName,
                Market: b.marketName,
                Selection: b.selection,
                Side: b.side,
                Odds: b.oddsMatched ?? b.oddsPlaced,
                Stake: b.stake,
                Matched: b.matched,
                Status: b.status,
                'Member P&L': b.pnl ?? '',
              })),
            )
          }
        />
      }
      footer={
        <Pager
          page={page}
          pageCount={Math.max(1, Math.ceil((data?.total ?? 0) / pageSize))}
          onChange={setPage}
          total={data?.total ?? 0}
          pageSize={pageSize}
        />
      }
    >
      <DataTable
        label={`Bets placed by ${account.username}`}
        rows={rows}
        columns={columns}
        rowKey={(b) => b.id}
        loading={isLoading}
        skeletonRows={10}
        showTotals={rows.length > 0}
        totalsLabel="This page"
        empty={
          <StateBlock icon="sport" title="No bets in this window">
            Bets placed between {range.from} and {range.to} would be listed here. Widen the date
            range in the top bar to look further back.
          </StateBlock>
        }
      />
    </Card>
  )
}

/* ============================================================ statement == */

function StatementTab({
  account,
  range,
  highlightRef,
}: {
  account: Account
  range: Range
  /** A ledger ref the operator arrived here looking for, from `?ref=`. */
  highlightRef?: string | null
}) {
  const { data, isLoading, isError, error, refetch } = useStatement(
    account.id,
    range,
    account.currencyType,
  )

  const rows = data?.rows ?? []
  const closing = data?.closingBalance ?? null
  const unavailable = Boolean(data?.unavailable)
  const reconciles =
    !unavailable && closing !== null && Math.abs(closing - account.balance) < 0.01

  /* A settlement line means opposite things on the two sides of the ladder. On a
     member's own ledger a positive amount is the member winning, which is the
     book paying out; on an agent's ledger the same column is their share of the
     book's result. Colour always follows the book, so only the member inverts. */
  const settlementInverts = account.role === 'player'

  /**
   * The row `?ref=` was asking for.
   *
   * The palette's transfer results land here, and a ref found in a search may
   * still be outside the shell's date range — so the two outcomes are told
   * apart explicitly. Saying nothing at all would be the worst option: the
   * operator followed a link naming a specific movement and would be left
   * scanning a table for it with no idea whether it was there.
   */
  const referenced = highlightRef ? rows.find((row) => row.ref === highlightRef) : undefined

  useEffect(() => {
    if (!referenced || isLoading) return
    const id = window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>('.w7-table tr[data-tone="warn"]')
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 80)
    return () => window.clearTimeout(id)
  }, [referenced, isLoading, highlightRef])

  if (isError) {
    return (
      <StateBlock
        icon="error"
        tone="error"
        title="The statement could not be loaded"
        actions={
          <Button variant="secondary" icon="refresh" onClick={() => void refetch()}>
            Try again
          </Button>
        }
      >
        {(error as Error)?.message}
      </StateBlock>
    )
  }

  return (
    <>
      {highlightRef && !isLoading ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          {referenced ? (
            <Banner tone="info" title={`Showing the statement around ${highlightRef}`}>
              {lll(referenced.at)} · {referenced.description}
              {referenced.counterparty ? ` · ${referenced.counterparty}` : ''} ·{' '}
              {money(referenced.amount, { signed: true, currency: account.currencyType, alreadyDisplay: true })}
              {referenced.balanceBefore != null ? (
                <>
                  {' '}
                  · previous{' '}
                  {money(referenced.balanceBefore, {
                    currency: account.currencyType,
                    alreadyDisplay: true,
                  })}
                </>
              ) : null}
              {' → '}
              {money(referenced.balanceAfter, { currency: account.currencyType, alreadyDisplay: true })}.
            </Banner>
          ) : (
            <Banner tone="warn" title={`${highlightRef} is not in this window`}>
              That reference is not among the movements between {range.from} and {range.to}. Widen
              the date range in the top bar to look further back — the movement is not missing, it
              is outside the window this table was asked for.
            </Banner>
          )}
        </div>
      ) : null}

      {unavailable ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <Banner tone="info" title="Statement not available yet">
            Live give/take history could not be loaded for this account. The balance at the top is
            still the hierarchy ledger; this table is empty until the statement source is reachable.
          </Banner>
        </div>
      ) : null}

      {closing !== null && !unavailable && !reconciles ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <Banner tone="error" title="This statement does not reconcile">
            The movements below close at {money(closing, { currency: account.currencyType, alreadyDisplay: true })}, but the balance at the top of this page is{' '}
            {money(account.balance, { currency: account.currencyType, alreadyDisplay: true })}. One of the two is wrong; treat the ledger statement as the
            record and raise the difference of {money(closing - account.balance, { signed: true, currency: account.currencyType, alreadyDisplay: true })}{' '}
            before moving any more credit.
          </Banner>
        </div>
      ) : null}

      <Card
        title="Statement"
        subtitle={`Every movement on this ledger in ${range.label}, newest first`}
        flush
        actions={
          <>
            {reconciles ? (
              <Chip tone="info" size="sm" icon="check">
                Reconciled
              </Chip>
            ) : null}
            <ExportCsvButton
              disabled={rows.length === 0}
              count={rows.length}
              onClick={() =>
                exportCsv(
                  `${account.username}-statement-${range.from}-to-${range.to}.csv`,
                  rows.map((r) => ({
                    Date: lll(r.at),
                    Description: r.description,
                    Counterparty: r.counterparty ?? '',
                    Ref: r.ref,
                    Amount: r.amount,
                    'Previous balance': r.balanceBefore ?? '',
                    'Next balance': r.balanceAfter,
                  })),
                )
              }
            />
          </>
        }
      >
        <div style={{ padding: 'var(--w7-s4)' }}>
          <div className="w7-preview">
            <span className="w7-preview-side">
              <span className="w7-micro">Opening balance</span>
              <Amount value={data?.openingBalance} currency={account.currencyType} alreadyDisplay />
            </span>
            <Icon name="arrow-right" className="w7-preview-arrow" size={18} />
            <span className="w7-preview-side" data-align="end">
              <span className="w7-micro">Closing balance</span>
              <Amount value={closing} currency={account.currencyType} alreadyDisplay />
            </span>
          </div>
          <p className="w7-card-sub" style={{ marginTop: 'var(--w7-s3)' }}>
            {reconciles
              ? `The closing balance is the ${money(account.balance, { currency: account.currencyType, alreadyDisplay: true })} shown at the top of this page. Every figure on this screen is the same ledger projection — nothing here is an optimistic local number.`
              : 'The closing balance must equal the balance shown at the top of this page.'}
          </p>
        </div>

        <DataTable
          label={`Account statement for ${account.username}`}
          rows={rows}
          rowKey={(r) => r.id}
          loading={isLoading}
          skeletonRows={10}
          rowTone={(r) => (highlightRef && r.ref === highlightRef ? 'warn' : undefined)}
          columns={[
            {
              id: 'at',
              header: 'Date',
              width: 148,
              cell: (r) => lll(r.at),
              sortValue: (r) => r.at,
            },
            {
              id: 'description',
              header: 'Description',
              cell: (r) => (
                <span className="w7-truncate" style={{ display: 'block' }} title={r.description}>
                  {r.description}
                </span>
              ),
              sortValue: (r) => r.description,
            },
            {
              id: 'counterparty',
              header: 'Counterparty',
              hideBelow: 900,
              cell: (r) => r.counterparty ?? EMPTY,
              sortValue: (r) => r.counterparty,
            },
            {
              id: 'ref',
              header: 'Ref',
              hideBelow: 1080,
              cell: (r) => <span className="w7-mono">{r.ref}</span>,
              sortValue: (r) => r.ref,
            },
            {
              id: 'amount',
              header: 'Amount',
              numeric: true,
              help: 'Credit movements are neutral. Settlements are coloured from the book’s point of view.',
              cell: (r) =>
                r.kind === 'settlement' ? (
                  <Money
                    value={r.amount}
                    signed
                    invert={settlementInverts}
                    currency={account.currencyType}
                    alreadyDisplay
                  />
                ) : (
                  <Money value={r.amount} signed tone="neutral" currency={account.currencyType} alreadyDisplay />
                ),
              sortValue: (r) => r.amount,
            },
            {
              id: 'balanceBefore',
              header: 'Previous balance',
              numeric: true,
              hideBelow: 900,
              help: 'Ledger balance immediately before this movement.',
              cell: (r) =>
                r.balanceBefore == null ? (
                  EMPTY
                ) : (
                  <Amount value={r.balanceBefore} currency={account.currencyType} alreadyDisplay />
                ),
              sortValue: (r) => r.balanceBefore,
            },
            {
              id: 'balanceAfter',
              header: 'Next balance',
              numeric: true,
              hideBelow: 720,
              help: 'Ledger balance immediately after this movement.',
              cell: (r) => (
                <Amount value={r.balanceAfter} currency={account.currencyType} alreadyDisplay />
              ),
              sortValue: (r) => r.balanceAfter,
            },
          ]}
          empty={
            <StateBlock icon="transfers" title="No movements in this window">
              Credit given, credit taken and settled positions between {range.from} and {range.to}{' '}
              would be listed here. Widen the date range in the top bar to look further back.
            </StateBlock>
          }
        />
      </Card>
    </>
  )
}

/* ============================================================= activity == */

function ActivityTab({ account }: { account: Account }) {
  const { data, isLoading } = useActivity(120, account.currencyType)
  const unavailable = data?.unavailable
  const events = (data?.rows ?? []).filter(
    (e) => e.actorId === account.id || e.subjectId === account.id,
  )


  return (
    <Card
      title="Activity"
      subtitle="Credit, limits, status and sign-ins on this account, newest first"
      flush
    >
      {unavailable ? (
        <StateBlock icon="info" title="Activity trail is not available yet">
          {unavailable.reason}
          {unavailable.trackedBy ? ` Tracked by ${unavailable.trackedBy}.` : null}
        </StateBlock>
      ) : isLoading ? (
        <div className="w7-col" style={{ gap: 12, padding: 'var(--w7-s4)' }}>
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} height={34} />
          ))}
        </div>
      ) : events.length === 0 ? (
        <StateBlock icon="activity" title="Nothing has happened yet">
          Credit movements, limit changes, status changes and sign-ins are recorded here as they
          happen.
        </StateBlock>
      ) : (
        <ul className="w7-list">
          {events.map((event) => (
            <li key={event.id}>
              <span className="w7-list-row">
                <Chip size="sm" tone={event.kind === 'transfer' ? 'info' : 'muted'}>
                  {ACTIVITY_LABEL[event.kind]}
                </Chip>
                <span style={{ minWidth: 0 }}>
                  <b>{event.summary}</b>
                  <span className="w7-dim" style={{ display: 'block', fontSize: 12 }}>
                    {lll(event.at)}
                    {event.actor ? ` · by ${event.actor.username}` : ''}
                    {event.detail ? ` · ${event.detail}` : ''}
                  </span>
                </span>
                {/* Live ActivityEntry has no IP/device; fixture may. Never render as
                    surveillance — non_goals[0]. */}
                {typeof event.amount === 'number' ? (
                  <Amount value={event.amount} currency={account.currencyType} alreadyDisplay />
                ) : (
                  <span />
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

/* =============================================================== credit == */

/**
 * Give and take credit.
 *
 * The replaced product moved money through a ~200px popup with two identical
 * buttons, no running balance and no idempotency, so a double click could move
 * twice and nothing on screen told you what the balance would become. This
 * drawer names the direction in its title, shows both balances before and after,
 * refuses amounts the ledger will reject, and carries one key per opening.
 */
function CreditDrawer({
  direction,
  idempotencyKey,
  self,
  target,
  onClose,
}: {
  direction: 'give' | 'take'
  idempotencyKey: string
  self: Account
  target: Account
  onClose: () => void
}) {
  const toast = useToast()
  const transfer = useTransferCredit()
  const [raw, setRaw] = useState('')
  const [remark, setRemark] = useState('')
  const [touched, setTouched] = useState(false)

  const giving = direction === 'give'
  const parsed = Number.parseFloat(raw)
  const amount = Number.isFinite(parsed) ? parsed : 0
  const childCcy = parseCurrencyType(target.currencyType)
  const selfCcy = parseCurrencyType(self.currencyType)
  const fmtChild = (n: number) => money(n, { currency: childCcy, alreadyDisplay: true })
  const fmtSelf = (n: number) => money(n, { currency: selfCcy, alreadyDisplay: true })

  const headroom = availableToGive(self.balance, self.exposure)
  const headroomChild = convertDisplayAmount(headroom, selfCcy, childCcy)
  const limitRoom = Math.max(0, round2(target.creditLimit - target.balance))
  const max = giving ? Math.min(headroomChild, limitRoom) : target.balance
  const amountInSelf = convertDisplayAmount(amount, childCcy, selfCcy)

  const problem =
    raw.trim() === ''
      ? null
      : !Number.isFinite(parsed) || parsed <= 0
        ? 'Enter an amount greater than zero.'
        : giving && parsed > headroomChild
          ? `You hold ${fmtSelf(headroom)} that is not already committed to open positions. Take credit back or wait for settlement first.`
          : giving && parsed > limitRoom
            ? `${target.username} can hold ${fmtChild(limitRoom)} more before reaching their ${fmtChild(target.creditLimit)} credit limit. Raise the limit first, or give less.`
            : !giving && parsed > target.balance
              ? `${target.username} holds ${fmtChild(target.balance)}. You cannot take more than they have.`
              : null

  const canSubmit = parsed > 0 && problem === null && !transfer.isPending
  const selfAfter = giving ? self.balance - amountInSelf : self.balance + amountInSelf
  const targetAfter = giving ? target.balance + amount : target.balance - amount
  const stripsCommitted = !giving && amount > 0 && targetAfter < target.exposure

  const presets = [20, 50, 100, 500, 1000, 5000].filter((p) => p <= max)

  const submit = () => {
    transfer.mutate(
      {
        accountId: target.id,
        amount: parsed,
        direction,
        remark: remark.trim(),
        idempotencyKey,
        currencyType: target.currencyType,
      },
      {
        onSuccess: (result) => {
          const theirBalance = giving ? result.to.balance : result.from.balance
          toast.success(
            `${giving ? 'Gave' : 'Took'} ${fmtChild(result.transfer.amount)} ${giving ? 'to' : 'from'} ${target.username}`,
            `Ref ${result.transfer.ref} · ${target.username} now holds ${fmtChild(theirBalance)}`,
          )
          onClose()
        },
        onError: (failure) => toast.error('The credit did not move', (failure as Error).message),
      },
    )
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={giving ? `Give credit to ${target.username}` : `Take credit from ${target.username}`}
      subtitle="Credit moves between you and them only. Balances refresh from the ledger once it lands — nothing here is shown as settled before it is."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={giving ? 'give' : 'take'} disabled={!canSubmit} busy={transfer.isPending} onClick={submit}>
            {giving ? 'Give' : 'Take'} {parsed > 0 ? fmtChild(parsed) : 'credit'}
          </Button>
        </>
      }
    >
      <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
        <Field
          label="Amount"
          required
          hint={
            giving
              ? `Most you can give now is ${fmtChild(max)} — the lower of your ${fmtSelf(headroom)} uncommitted and the ${fmtChild(limitRoom)} still free under their credit limit.`
              : `Most you can take now is ${fmtChild(max)} — everything they currently hold.`
          }
          error={touched ? problem : null}
          aside={
            /* Floored, never rounded: a chip that quotes ₹6,786 when the ledger
               will only accept ₹6,785.56 is a papercut you hit at the Confirm. */
            <Chip size="sm" tone="muted">
              Max {fmtChild(Math.floor(max))}
            </Chip>
          }
        >
          {({ id, describedBy, invalid }) => (
            <MoneyInput
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              currency={target.currencyType}
              value={raw}
              autoFocus
              onChange={(event) => setRaw(event.target.value)}
              onBlur={() => setTouched(true)}
            />
          )}
        </Field>

        {presets.length > 0 ? (
          <PresetAmounts
            presets={presets}
            format={(value) => fmtChild(value)}
            onPick={(value) => {
              setRaw(String(value))
              setTouched(true)
            }}
          />
        ) : null}

        <Field label="Remark" hint="Appears on both statements and in the activity log.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={remark}
              maxLength={80}
              placeholder={giving ? 'e.g. Top-up for the weekend' : 'e.g. Weekly settlement'}
              onChange={(event) => setRemark(event.target.value)}
            />
          )}
        </Field>

        {/* Both sides, before and after, at the same precision — the old popup
            showed neither balance, so you confirmed a movement without knowing
            what either side would be left holding. */}
        <div className="w7-preview">
          <span className="w7-preview-side">
            <span className="w7-micro">You after</span>
            <Amount value={selfAfter} currency={self.currencyType} alreadyDisplay />
            <span className="w7-dim" style={{ fontSize: 11.5 }}>
              now {fmtSelf(self.balance)}
            </span>
          </span>
          <Icon name="arrow-right" className="w7-preview-arrow" size={18} />
          <span className="w7-preview-side" style={{ alignItems: 'flex-end' }}>
            <span className="w7-micro">{target.username} after</span>
            <Amount value={targetAfter} currency={target.currencyType} alreadyDisplay />
            <span className="w7-dim" style={{ fontSize: 11.5 }}>
              now {fmtChild(target.balance)}
            </span>
          </span>
        </div>

        {stripsCommitted ? (
          <Banner tone="warn" title="This takes credit they have already committed">
            {target.username} has {fmtChild(target.exposure)} riding on open positions. Leaving them
            with {fmtChild(targetAfter)} means their next bets will be rejected until those settle.
          </Banner>
        ) : null}
      </div>
    </Drawer>
  )
}
